import { useCallback, useMemo, useState } from 'react';

import { Page } from '../components/ImplementationBase/types';

/**
 * Custom hook to manage timeline navigation between pages.
 * @param {Page[]} pages - Array of page objects.
 * @returns {Object} - Functions and state for timeline navigation.
 */
export const useTimeline = (pages: Page[]) => {
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

  const currentPage = pages[currentPageIndex];

  /**
   * Move to the next page.
   */
  const goForward = useCallback(() => {
    setCurrentPageIndex(prevIndex =>
      prevIndex < pages.length - 1 ? prevIndex + 1 : prevIndex,
    );
  }, [pages.length]);

  /**
   * Move to the previous page.
   */
  const goBackward = useCallback(() => {
    setCurrentPageIndex(prevIndex =>
      prevIndex > 0 ? prevIndex - 1 : prevIndex,
    );
  }, []);

  /**
   * Navigate to a specific page.
   * @param {string} pageKey - Key of the page to navigate to.
   */
  const goTo = useCallback(
    (pageKey: string) => {
      const pageIndex = pages.findIndex(page => page.key === pageKey);
      if (pageIndex !== -1) {
        setCurrentPageIndex(pageIndex);
      }
    },
    [pages],
  );

  /**
   * Reset the timeline to the initial state.
   */
  const reset = useCallback(() => {
    setCurrentPageIndex(0);
  }, []);

  const validKeys = useMemo(() => {
    return pages.map(page => page.key);
  }, [pages]);

  return {
    goForward,
    goBackward,
    goTo,
    reset,
    currentPage,
    currentPageIndex,
    validKeys,
  };
};
